import { useTemplateOrCalculation } from '../../../../../hooks/useTemplateOrCalculation';
import { useGetEvaluationSummaryQuery } from '../../apiCalculationEvaluations';
import { Column, ReadOnlyItem, Row, Section, SectionHeader, Title } from '@ydistri/ds';
import React, { useCallback, useMemo, useState } from 'react';
import CalculationInfo from './CalculationInfo';
import StoreEvaluationSummarySubSection from './StoreEvaluationSummarySubSection';
import PickingProgressInfo from './PickingProgressInfo/PickingProgressInfo';
import ExecutionRatesInfo from './ExecutionRatesInfo/ExecutionRatesInfo';
import RateTag from './RateTag/RateTag';
import { styled } from 'styled-components';
import { useSelector } from 'react-redux';
import { ReduxState } from '../../../../../store';
import { useApplicationData } from '../../../../../hooks/useApplicationData';
import { COLORS } from '@ydistri/theme';

interface EvaluationSummaryContent {
  title: string;
  content: React.ReactNode;
  flexGrow: number;
  minWidth?: string;
}

const ClickableRow = styled(Row)`
  cursor: pointer;
`;

const CalculationEvaluationSummary: React.FC = () => {
  const templateOrCalculation = useTemplateOrCalculation();
  const clusterType = useSelector((state: ReduxState) => state.evaluations.clusterType);
  const { skuDeliveryData: hasSkuDeliveryData } = useApplicationData();

  const { data, isFetching: isFetchingStatistics } = useGetEvaluationSummaryQuery({
    calculationId: templateOrCalculation.id,
    clusterType: clusterType,
  });

  const [sectionOpen, setSectionOpen] = useState<boolean>(true);

  const evaluationSummaryContent = useMemo((): EvaluationSummaryContent[] => {
    return [
      {
        title: 'Calculation',
        flexGrow: 0,
        minWidth: '376px',
        content: (
          <CalculationInfo
            loading={isFetchingStatistics}
            createdAt={data?.calculationCreatedAt}
            finishExecutionUntil={data?.finishExecutionUntil}
            executionType={data?.executionType}
            status={data?.pickingProgress.status}
          />
        ),
      },
      {
        title: 'Picking progress',
        flexGrow: 0,
        minWidth: '476px',
        content: !isFetchingStatistics && data && (
          <PickingProgressInfo
            pickingProgress={data.pickingProgress}
            showInProgressRoutes={data.isPap}
          />
        ),
      },
      {
        title: 'Execution rates',
        flexGrow: 0,
        minWidth: hasSkuDeliveryData ? '584px' : '364px',
        content: !isFetchingStatistics && data && (
          <ExecutionRatesInfo data={data.executionStatus} />
        ),
      },
    ];
  }, [data, hasSkuDeliveryData, isFetchingStatistics]);

  const toggleSectionOpen = useCallback(() => {
    setSectionOpen(prev => !prev);
  }, []);

  const sectionHeader = useMemo(() => {
    const content = (
      <ClickableRow $gap="2rem" $alignItems="baseline" onClick={toggleSectionOpen} $flexGrow={1}>
        <Title $size="large" $semiBold>
          Summary
        </Title>
        {!sectionOpen && (
          <>
            <ReadOnlyItem label="Status" value={data?.pickingProgress.status.valueOf()} />
            <ReadOnlyItem
              label="Picking progress"
              value={
                <RateTag value={data?.pickingProgress.rate} title="Total picking progress rate" />
              }
            />
          </>
        )}
      </ClickableRow>
    );

    return (
      <SectionHeader
        data-type="section-header"
        open={sectionOpen}
        collapsible={true}
        setOpen={setSectionOpen}
        showBulletPoint={true}
      >
        {content}
      </SectionHeader>
    );
  }, [data?.pickingProgress.rate, data?.pickingProgress.status, sectionOpen, toggleSectionOpen]);

  return (
    <Section
      collapsible={true}
      open={sectionOpen}
      onOpen={setSectionOpen}
      header={sectionHeader}
      $contentMarginTop={false}
      $headerMarginTop={false}
      $backgroundColor={COLORS.GREY_LIGHT}
      $ratio={0}
    >
      <Row $gap="2rem" data-type="calculation-evaluation-summary-components" $minHeight="280px">
        {evaluationSummaryContent.map(item => {
          return (
            <Column key={`calculation-evaluation-summary-${item.title}`} $flexGrow={item.flexGrow}>
              <StoreEvaluationSummarySubSection
                title={item.title}
                loading={isFetchingStatistics}
                minWidth={item.minWidth}
              >
                {item.content}
              </StoreEvaluationSummarySubSection>
            </Column>
          );
        })}
      </Row>
    </Section>
  );
};

export default CalculationEvaluationSummary;
